import { useMemo, useRef, useState } from "react";
import { Paperclip, Image, UploadSimple, Trash, File } from "@phosphor-icons/react";
import { toast } from "sonner";
import { isImagePath, humanFileSize } from "@/lib/storage";

/**
 * Shared file upload card used by daily-report photos, incident attachments
 * and enrollment documents.
 *
 * Props:
 *   - accept: input[accept] string (e.g. "image/*" or ".pdf,.jpg")
 *   - mode: "image" | "file" — controls the icon + copy
 *   - items: [{ id, storage_path, caption?, document_type?, created_at }]
 *   - onUpload(file): Promise — parent performs the live upload + returns new item
 *   - onRemove(item): Promise — parent deletes item + storage object
 *   - resolveSignedUrl(path): Promise<string>
 *   - canEdit: boolean — hide upload/delete controls when false
 *   - maxSizeMb: number — client-side guard (default 10)
 *   - label: section title
 *   - emptyLabel: what to show when items is []
 *   - testId: root test id prefix
 */
export default function FileUploadCard({
  accept = "image/*",
  mode = "image",
  items = [],
  onUpload,
  onRemove,
  resolveSignedUrl,
  canEdit = true,
  maxSizeMb = 10,
  label = "Attachments",
  emptyLabel = "No files yet.",
  testId = "file-upload",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [signedUrls, setSignedUrls] = useState({}); // { path: url }
  const [errored, setErrored] = useState({}); // { path: true }

  const isImageMode = mode === "image";
  const MainIcon = isImageMode ? Image : Paperclip;

  const triggerFilePicker = () => inputRef.current?.click();

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const maxBytes = maxSizeMb * 1024 * 1024;
    for (const f of files) {
      if (f.size > maxBytes) {
        toast.error(`${f.name} is larger than ${maxSizeMb}MB`);
        continue;
      }
      setUploading(true);
      try {
        await onUpload(f);
        toast.success(`${f.name} uploaded`);
      } catch (err) {
        toast.error(err?.message || `Could not upload ${f.name}`);
      } finally {
        setUploading(false);
      }
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const resolveOne = async (path) => {
    if (signedUrls[path] || errored[path]) return;
    try {
      const url = await resolveSignedUrl(path);
      setSignedUrls((s) => ({ ...s, [path]: url }));
    } catch (err) {
      setErrored((s) => ({ ...s, [path]: true }));
      // eslint-disable-next-line no-console
      console.warn("Signed URL failed:", path, err?.message);
    }
  };

  const handleRemove = async (item) => {
    if (!window.confirm("Remove this file?")) return;
    try {
      await onRemove(item);
      toast.success("File removed");
      setSignedUrls((s) => {
        const next = { ...s };
        delete next[item.storage_path];
        return next;
      });
    } catch (err) {
      toast.error(err?.message || "Could not remove file");
    }
  };

  const hasItems = items.length > 0;
  const idBase = useMemo(() => testId, [testId]);

  return (
    <section
      data-testid={idBase}
      className="rounded-2xl border border-neutral-200 bg-white p-5"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MainIcon
            size={18}
            weight="duotone"
            className="text-primary-hover"
          />
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            {label}
          </p>
          {hasItems && (
            <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-700">
              {items.length}
            </span>
          )}
        </div>
        {canEdit && (
          <button
            type="button"
            data-testid={`${idBase}-upload-btn`}
            disabled={uploading}
            onClick={triggerFilePicker}
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary-hover transition-all hover:bg-primary/10 disabled:opacity-60"
          >
            <UploadSimple size={14} weight="bold" />
            {uploading ? "Uploading…" : "Upload"}
          </button>
        )}
      </div>

      {canEdit && (
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          hidden
          data-testid={`${idBase}-input`}
          onChange={(e) => handleFiles(e.target.files)}
        />
      )}

      {!hasItems ? (
        <p className="rounded-xl bg-neutral-50 px-3 py-4 text-center text-xs text-neutral-500">
          {emptyLabel}
        </p>
      ) : (
        <ul
          data-testid={`${idBase}-list`}
          className={
            isImageMode
              ? "grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4"
              : "space-y-2"
          }
        >
          {items.map((item) => {
            const path = item.storage_path;
            const url = signedUrls[path];
            const failed = errored[path];
            const imgLike = isImageMode && isImagePath(path);

            if (!url && !failed) resolveOne(path);

            if (imgLike) {
              return (
                <li
                  key={item.id}
                  data-testid={`${idBase}-item-${item.id}`}
                  className="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-100 aspect-square"
                >
                  {url ? (
                    <img
                      src={url}
                      alt={item.caption || "Daycare photo"}
                      className="h-full w-full object-cover"
                    />
                  ) : failed ? (
                    <div className="flex h-full w-full items-center justify-center p-2 text-center text-[11px] text-neutral-500">
                      Could not load preview
                    </div>
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <div className="h-6 w-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                    </div>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      data-testid={`${idBase}-remove-${item.id}`}
                      onClick={() => handleRemove(item)}
                      className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-[#C6851E] opacity-0 shadow-sm backdrop-blur transition-opacity group-hover:opacity-100 focus:opacity-100"
                      aria-label="Remove photo"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                </li>
              );
            }

            return (
              <li
                key={item.id}
                data-testid={`${idBase}-item-${item.id}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-white px-3 py-2.5"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-secondary/30">
                    <File size={16} weight="duotone" className="text-neutral-700" />
                  </span>
                  <div className="min-w-0">
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`${idBase}-open-${item.id}`}
                        className="block truncate text-sm font-semibold text-neutral-900 hover:text-primary-hover"
                      >
                        {item.document_type ||
                          item.caption ||
                          fileLabelFromPath(path)}
                      </a>
                    ) : (
                      <p className="truncate text-sm font-semibold text-neutral-700">
                        {item.document_type ||
                          item.caption ||
                          fileLabelFromPath(path)}
                      </p>
                    )}
                    <p className="mt-0.5 text-[11px] text-neutral-500">
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "—"}
                      {item.size_bytes ? ` · ${humanFileSize(item.size_bytes)}` : ""}
                    </p>
                  </div>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    data-testid={`${idBase}-remove-${item.id}`}
                    onClick={() => handleRemove(item)}
                    className="rounded-full p-2 text-neutral-400 transition-colors hover:bg-[#C6851E]/10 hover:text-[#C6851E]"
                    aria-label="Remove file"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function fileLabelFromPath(path) {
  if (!path) return "File";
  const basename = path.split("/").pop() || "File";
  // strip the random id prefix to show only the extension-ish hint
  return basename.length > 28 ? basename.slice(0, 26) + "…" : basename;
}
